// /admin — owner-only analytics for the StreamNook user base. Real access
// control is server-side in functions/api/admin/analytics.ts (session subject
// must equal ADMIN_TWITCH_USER_ID); this page also self-gates on me.is_admin so
// a non-admin never even fires the request. Replaces the dashboard that used to
// be embedded in the desktop binary and served over localhost.

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ComponentType, CSSProperties, ReactNode } from 'react';
// Phosphor throughout (Brandon's rule: no lucide). The older panels keep their
// local names through aliases, so only the import changed.
import {
  Pulse as Activity, ArrowsClockwise as RefreshCw, MagnifyingGlass as Search, ArrowSquareOut as ExternalLink,
  Clock, TrendUp as TrendingUp, Users, ChatCenteredText as MessageSquare, Package, Broadcast as Radio, UserPlus,
  Crown, Gift, X, CaretDown as ChevronDown, CaretUp as ChevronUp, CaretLeft as ChevronLeft,
  CaretRight as ChevronRight, ArrowUp, ArrowDown, DownloadSimple as Download, Monitor, Globe,
  ArrowSquareOut, Flag, Prohibit, Trash,
} from '@phosphor-icons/react';
import { SiAndroid, SiKick, SiTwitch, SiYoutube } from '@icons-pack/react-simple-icons';
import { useAuth } from '../lib/auth';
import AdminMembership from '../components/AdminMembership';
import type { AdminReport } from '../../functions/api/admin/profile-reports';
import { REPORT_REASONS } from '../profile/page/reportReasons';
import { profileUrl } from '../lib/profileUrl';
import { accoladeDef, accoladeLabel, sortAccoladeIds } from '../lib/accolades';
import {
  fetchMemberCosmetics,
  ownedForDisplay,
  type MemberCosmetics,
} from '../lib/memberCosmetics';
import {
  fetchAdminAnalytics,
  computeActivitySummary,
  computeVersionDistribution,
  computePlatformVersions,
  computeUpdateHealth,
  computeAdoptionCurve,
  subscribeToOnlinePresence,
  emptyPresenceSnapshot,
  giftMembership,
  revokeMembership,
  type AdminAnalytics,
  type AdminUser,
  type Membership,
  type GiftTerm,
  type OnlinePresenceSnapshot,
  type OnlineUser,
  type VersionRow,
  type PlatformVersions,
  type UpdateHealth,
} from '../lib/adminAnalytics';
import TwitchSignInLabel from '../components/TwitchSignInLabel';

// ─── Formatting ──────────────────────────────────────────────────────────────

const formatRelativeTime = (dateString: string): string => {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (diffMs < 30000) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString();
};

const formatNumber = (n: number): string => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toLocaleString();
};

const formatHours = (h: number): string => {
  if (h >= 1000) return (h / 1000).toFixed(1) + 'k';
  if (h < 1) return h.toFixed(2);
  return h.toFixed(1);
};

// ─── Membership ────────────────────────────────────────────────────────────

const GIFT_TERMS: { value: GiftTerm; label: string }[] = [
  { value: 1, label: '1 month' },
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
  { value: null, label: 'Permanent' },
];

type PillTone = 'active' | 'ending' | 'gift' | 'lapsed' | 'none';

const membershipPill = (m: Membership): { label: string; tone: PillTone } => {
  if (m.is_active && m.source === 'gift') return { label: 'Gifted', tone: 'gift' };
  if (m.is_active && m.source === 'stripe' && m.cancel_at_period_end) return { label: 'Ending', tone: 'ending' };
  if (m.is_active) return { label: m.status === 'past_due' ? 'Past due' : 'Active', tone: 'active' };
  if (m.owns_subscriber_badge) return { label: 'Lapsed', tone: 'lapsed' };
  if (m.status === 'canceled') return { label: 'Canceled', tone: 'lapsed' };
  return { label: 'None', tone: 'none' };
};

const PILL_CLASS: Record<PillTone, string> = {
  active: 'border-emerald-400/20 bg-emerald-400/10 text-emerald-400',
  ending: 'border-amber-400/20 bg-amber-400/10 text-amber-300',
  gift: 'border-accent/30 bg-accent/10 text-accent',
  lapsed: 'border-white/10 bg-white/[0.04] text-textSecondary',
  none: 'border-transparent text-textMuted',
};

/** "Jan 5, 2026 · 158 days" for membership-anchor timestamps. */
const dateWithDaysAgo = (iso: string): string => {
  const date = new Date(iso);
  const days = Math.max(0, Math.floor((Date.now() - date.getTime()) / 86_400_000));
  return `${date.toLocaleDateString()} · ${days.toLocaleString()} ${days === 1 ? 'day' : 'days'}`;
};

const giftSummary = (m: Membership): string => {
  if (m.source !== 'gift') return '';
  // Checked before the null test: a boost has no remaining count either, but it
  // lasts only as long as the boost, so it is not a permanent gift.
  if (m.gift_source === 'boost') return 'Boosting';
  if (m.gift_months_remaining === null) return 'Permanent gift';
  const left = m.gift_months_remaining;
  return left > 0 ? `${left} more month${left === 1 ? '' : 's'} to accrue` : 'Final gifted month';
};

// ─── Stat card ───────────────────────────────────────────────────────────────

/** Brand identity for everything that can show up in the Platforms column.
 *  Keyed by the string the app sends, so an unknown platform still renders its
 *  own name rather than disappearing from the report.
 *
 *  ACCOUNTS ONLY as of 2026-09-19. This used to also carry the client platform,
 *  merged into one icon row on the argument that the reader does not care about
 *  the distinction. They do: once clients report a real OS, "which platform is
 *  stuck on an old build" is the whole question, and an icon row that mixes "has
 *  a Kick account" with "runs Android" cannot answer it. Clients now have their
 *  own column, fed by `client_details`. */
const PLATFORM_META: Record<string, { label: string; color: string; icon: ComponentType<{ className?: string; style?: CSSProperties }> }> = {
  twitch: { label: 'Twitch', color: '#9146ff', icon: SiTwitch },
  kick: { label: 'Kick', color: '#53fc18', icon: SiKick },
  youtube: { label: 'YouTube', color: '#ff0000', icon: SiYoutube },
  android: { label: 'Android', color: '#3ddc84', icon: SiAndroid },
};

/** How to name a client platform in prose.
 *
 *  These are Rust's own `std::env::consts::OS` values, which is also the key space
 *  the update manifest uses, so a row here lines up with an update artifact. The
 *  legacy `desktop` is every client that signed in before the heartbeat shipped:
 *  those rows are kept rather than rewritten, because "used a desktop build" is
 *  true and inventing an OS we never observed would not be. They decay as those
 *  members' clients report again under a specific OS. */
const CLIENT_PLATFORM_LABEL: Record<string, string> = {
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
  android: 'Android',
  ios: 'iOS',
  desktop: 'Desktop',
  web: 'Website',
};

/** The long form, for tooltips and the profile panel where there is room.
 *
 *  `desktop` deliberately reads short in the table. Spelled out it is 23
 *  characters of `whitespace-nowrap` on essentially EVERY row (227 of 228 client
 *  rows are still the legacy value), which pushed the table 170px wider on its
 *  own and helped make the right-hand columns unreachable. The explanation
 *  belongs on hover, not in every cell. */
const CLIENT_PLATFORM_TITLE: Record<string, string> = {
  desktop: 'Desktop, signed in before the version-reporting heartbeat shipped, so the exact OS is unknown',
};

const clientPlatformLabel = (p: string): string => CLIENT_PLATFORM_LABEL[p] ?? p;
const clientPlatformTitle = (p: string): string =>
  CLIENT_PLATFORM_TITLE[p] ?? clientPlatformLabel(p);

/** Everything to mark for one row, in a fixed order so the column stays a
 *  column and marks do not shuffle between rows.
 *
 *  Twitch is on every row because every row IS a Twitch account: this reads as
 *  "uses Twitch, and also Kick", which is the honest picture.
 *
 *  Client platforms are NOT here any more; see the Client column. */
const platformsFor = (user: AdminUser): string[] => [
  'twitch',
  ...user.linked_platforms.filter(p => p !== 'twitch'),
];

/** The client platforms a member has signed in from, deduped and ordered.
 *
 *  From the PERSISTED list, never from presence: the presence payload is an
 *  unauthenticated websocket write, so anyone holding the publishable key could
 *  add a platform to any member (H14). A member seen on a phone but not signed in
 *  since the release that records clients shows nothing until they are.
 *
 *  Unlike the old Platforms column, `desktop` IS shown. It used to be hidden as
 *  "the default, so a mark on nearly every row carries no information", which was
 *  true while it was the only desktop value. Now it means specifically "signed in
 *  before the heartbeat shipped", which is exactly the cohort worth seeing. */
const clientsFor = (user: AdminUser): string[] => {
  const seen = new Set<string>();
  for (const c of user.client_details ?? []) seen.add(c.platform);
  // Fall back to the flat list for a DB without the star-selectable detail rows.
  if (seen.size === 0) for (const p of user.clients ?? []) seen.add(p);
  return [...seen].sort();
};

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: ComponentType<{ size?: number; className?: string }>;
}

const StatCard = ({ title, value, subtitle, icon: Icon }: StatCardProps) => (
  <div className="settings-card p-5">
    <div className="mb-3 inline-flex rounded-lg bg-white/[0.04] p-2.5 text-accent">
      <Icon size={18} />
    </div>
    <div className="text-3xl font-semibold tracking-tight tabular-nums text-textPrimary">{value}</div>
    <div className="mt-1 text-sm font-medium text-textSecondary">{title}</div>
    {subtitle && <div className="mt-1 text-xs text-textMuted">{subtitle}</div>}
  </div>
);

// ─── Version distribution ────────────────────────────────────────────────────

const VersionDistribution = ({
  rows,
  total,
  limit = 8,
}: {
  rows: VersionRow[];
  total: number;
  limit?: number;
}) => {
  if (rows.length === 0) return <div className="text-sm text-textMuted">No version data yet.</div>;
  // The LARGEST bucket, not rows[0]. `rows` is sorted by VERSION, so rows[0] is
  // the newest release, which is usually a small early-adopter bucket: bars were
  // normalized against it and routinely blew past 100% of their track (3 clients
  // on the newest build against 100 on an older one gave a 3333%-wide bar), while
  // the percentage text beside them stayed correct. The two disagreed on screen.
  const max = Math.max(1, ...rows.map(r => r.count));
  return (
    <div className="space-y-3">
      {rows.slice(0, limit).map(r => {
        const pct = total > 0 ? (r.count / total) * 100 : 0;
        const widthPct = (r.count / max) * 100;
        return (
          <div key={r.version}>
            <div className="mb-1 flex justify-between text-xs">
              <span className="font-mono text-textSecondary">{r.version}</span>
              <span className="text-textMuted">
                {r.count} <span className="opacity-60">({pct.toFixed(0)}%)</span>
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.05]">
              <div className="h-full rounded-full bg-accent/70" style={{ width: `${widthPct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Earned assets (profile panel) ───────────────────────────────────────────

/** What a member has actually earned: cosmetics with their real art, accolades
 *  with their real names, and their member number.
 *
 *  Cosmetics are fetched per member from the public
 *  /api/v1/cosmetics/users?expand=1 rather than loaded for all 533 members in the
 *  analytics call, which is only ever read one row at a time. Accolades ride the
 *  analytics response, because they are two small columns.
 *
 *  Loads when a row is expanded, and shows its own failure: this panel is the only
 *  place the information exists, so silently rendering nothing would look
 *  identical to a member who owns nothing. */
const EarnedAssets = ({ user }: { user: AdminUser }) => {
  const [cos, setCos] = useState<MemberCosmetics | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    const ac = new AbortController();
    setState('loading');
    setCos(null);
    fetchMemberCosmetics(user.id, ac.signal)
      .then(c => { setCos(c); setState('ready'); })
      .catch(e => { if (!ac.signal.aborted) setState('failed'); void e; });
    return () => ac.abort();
  }, [user.id]);

  const items = cos ? ownedForDisplay(cos) : [];
  const accolades = sortAccoladeIds(user.accolades ?? []);

  return (
    <div className="space-y-4 border-t border-white/5 pt-4">
      <div className="flex items-baseline justify-between gap-3">
        <h4 className="text-xs font-semibold uppercase tracking-widest text-textMuted">Earned</h4>
        {cos?.member_number != null && (
          <span className="text-xs text-textMuted">
            Member <span className="font-mono text-textSecondary">#{cos.member_number}</span>
          </span>
        )}
      </div>

      {state === 'loading' && (
        <p className="text-xs text-textMuted">Loading cosmetics…</p>
      )}
      {state === 'failed' && (
        <p className="text-xs text-amber-400">
          Could not load cosmetics. Accolades below are unaffected.
        </p>
      )}
      {state === 'ready' && cos && !cos.member && (
        <p className="text-xs text-textMuted">
          Not a StreamNook member yet, so nothing is granted. Signing in once issues a member
          number and the default badge.
        </p>
      )}

      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map(c => (
            <div
              key={c.slug}
              title={`${c.name}${c.description ? ` — ${c.description}` : ''}${c.equipped ? ' (equipped)' : ''}`}
              className={`flex min-w-0 items-center gap-2 rounded-lg border px-2.5 py-1.5 ${
                c.equipped
                  ? 'border-accent/30 bg-accent/10'
                  : 'border-white/5 bg-white/[0.03]'
              }`}
            >
              {/* The real asset, from the expanded side map. Sized like chat,
                  since that is where these are actually worn. An atmosphere
                  built purely from CSS gradients has no art at all, so it gets
                  a marked tile rather than a broken image: `src={null}` renders
                  as a failed load, which reads as "we lost the picture" when
                  the truth is there was never one. */}
              {c.image_url ? (
                <img
                  src={c.image_url}
                  alt=""
                  loading="lazy"
                  className="h-5 w-5 flex-shrink-0 object-contain"
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="h-5 w-5 flex-shrink-0 rounded-full border border-white/10 bg-gradient-to-br from-accent/40 to-accent/5"
                />
              )}
              <span className="truncate text-xs text-textSecondary">{c.name}</span>
              {c.equipped && (
                <span className="flex-shrink-0 text-[10px] uppercase tracking-wide text-accent">
                  worn
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {state === 'ready' && cos?.member && items.length === 0 && (
        <p className="text-xs text-textMuted">No cosmetics granted.</p>
      )}

      <div>
        <div className="mb-2 text-xs text-textMuted">
          Accolades{' '}
          <span className="text-textSecondary">{accolades.length || 'none'}</span>
          {/* Said out loud, because a short list here is not the same as a short
              list in the app: the desktop also shows ~20 accolades derived from
              stats thresholds, and those are never written to the database. */}
          <span className="opacity-60"> · collected ones only, not stat milestones</span>
        </div>
        {accolades.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {accolades.map(id => {
              const def = accoladeDef(id);
              return (
                <span
                  key={id}
                  title={def ? def.hint : `Unrecognised accolade id: ${id}`}
                  className={`rounded-md border px-2 py-0.5 text-xs ${
                    def
                      ? 'border-white/5 bg-white/[0.03] text-textSecondary'
                      : 'border-amber-400/20 bg-amber-400/5 text-amber-300'
                  }`}
                >
                  {accoladeLabel(id)}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Per-platform versions ───────────────────────────────────────────────────

/** One histogram per client platform, from `user_clients`.
 *
 *  The flat `Version distribution` panel reads `users.app_version`, which is one
 *  column shared by desktop 8.x and Android 0.1.x and written once per login. That
 *  makes it useless for the question this panel exists to answer: pooled and
 *  sorted descending, every desktop version outranks every Android version, and a
 *  top-8 slice dropped Android off the screen entirely. */
const PlatformVersionPanel = ({ platforms }: { platforms: PlatformVersions[] }) => {
  if (platforms.length === 0) {
    return (
      <div className="text-sm text-textMuted">
        No client is reporting its platform yet. This fills in as members run a build with
        the telemetry heartbeat.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {platforms.map(p => (
        <div key={p.platform}>
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <span className="text-sm font-medium text-textSecondary">
              {clientPlatformLabel(p.platform)}
            </span>
            <span className="text-xs text-textMuted">
              {p.newest ? (
                <>
                  <span className="tabular-nums text-textSecondary">
                    {Math.round((p.onNewest / Math.max(1, p.total)) * 100)}%
                  </span>{' '}
                  on {p.newest} &middot; {p.total} {p.total === 1 ? 'client' : 'clients'}
                </>
              ) : (
                <>{p.total} {p.total === 1 ? 'client' : 'clients'}, no version reported</>
              )}
            </span>
          </div>
          <VersionDistribution rows={p.rows} total={p.total} limit={5} />
        </div>
      ))}
    </div>
  );
};

// ─── Update health ───────────────────────────────────────────────────────────

/** Why the clients that are behind are behind.
 *
 *  Before this existed the data could name who was on an old build and say nothing
 *  at all about why, because nothing recorded whether a client had even checked. A
 *  successful check that found nothing wrote no line anywhere, so "checked and
 *  current", "check hung forever", "the timer was reset before it fired" and "the
 *  component that owns the timer threw" were one observable: silence.
 *
 *  The rows are deliberately not mutually exclusive: a client can be failing its
 *  checks AND be on an old build. They are diagnoses, not a partition. */
const UpdateHealthPanel = ({ health }: { health: UpdateHealth }) => {
  if (health.clients === 0) {
    return <div className="text-sm text-textMuted">No active clients in the last 7 days.</div>;
  }

  const rows: { label: string; value: number; hint: string; tone: 'bad' | 'warn' | 'info' }[] = [
    {
      label: 'Never checked',
      value: health.neverChecked,
      hint: 'The update check has not completed once on this client.',
      tone: 'bad',
    },
    {
      label: 'Check failing',
      value: health.checkFailing,
      hint: 'Trying and cannot reach the update server.',
      tone: 'bad',
    },
    {
      label: 'No build published',
      value: health.notPublishedFor,
      hint: 'Checking correctly and being offered nothing for their OS and architecture.',
      tone: 'warn',
    },
    {
      label: 'Offered, not taken',
      value: health.offeredAndIgnored,
      hint: 'They are seeing the prompt and have not acted on it.',
      tone: 'warn',
    },
    {
      label: 'Recovered reporters',
      value: health.recoveredReporters,
      hint: 'Reported after one or more failed heartbeats, so they were invisible for a while.',
      tone: 'info',
    },
    {
      label: 'Not reporting',
      value: health.notReporting,
      hint: 'Running a build too old to report update health, or the migration has not been applied.',
      tone: 'info',
    },
  ];

  const toneClass = {
    bad: 'text-red-400',
    warn: 'text-amber-400',
    info: 'text-textMuted',
  } as const;

  return (
    <div className="space-y-3">
      <p className="text-xs text-textMuted">
        {health.clients} {health.clients === 1 ? 'client' : 'clients'} active in the last 7 days.
      </p>
      <div className="space-y-2">
        {rows.map(r => (
          <div key={r.label} className="flex items-baseline justify-between gap-3" title={r.hint}>
            <span className="text-xs text-textSecondary">{r.label}</span>
            <span
              className={`font-mono text-xs tabular-nums ${r.value > 0 ? toneClass[r.tone] : 'text-textMuted opacity-40'}`}
            >
              {r.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Live users ──────────────────────────────────────────────────────────────

// Presence supplies WHO is connected and nothing else (see OnlineUser). Names,
// versions and platforms are resolved from the server-side directory instead,
// because the presence payload is an unauthenticated websocket write that
// anyone holding the publishable key can forge (H14).
const LiveUsersPanel = ({
  snapshot,
  usersById,
}: {
  snapshot: OnlinePresenceSnapshot;
  usersById: Map<string, AdminUser>;
}) => {
  if (!snapshot.ready) {
    return (
      <div className="flex items-center gap-2 text-sm text-textMuted">
        <RefreshCw size={14} className="animate-spin" />
        Connecting to presence…
      </div>
    );
  }
  if (snapshot.totalUnique === 0) {
    return <div className="text-sm text-textMuted">No one is online right now.</div>;
  }
  return (
    <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
      {snapshot.authedUsers.map((u: OnlineUser) => {
        const known = usersById.get(u.user_id);
        return (
        <div key={u.user_id} className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-white/[0.02]">
          <div className="h-2 w-2 flex-shrink-0 rounded-full bg-emerald-400" />
          <div className="min-w-0 flex-1">
            {/* A presence id that resolves to nobody is a fabricated entry, so
                it is shown as the raw id rather than any name it claimed. */}
            <div className="truncate text-sm text-textPrimary" title={known?.display_name || known?.username || u.user_id}>{known?.display_name || known?.username || u.user_id}</div>
            <div className="text-xs text-textMuted">online {formatRelativeTime(u.online_at)}</div>
          </div>
          {/* Which client they are on, next to the version it belongs to.
              Android is called out because that is the build being beta-tested
              and the one worth spotting at a glance; desktop is left unlabelled
              so the common case stays quiet. Both come from `user_clients` via
              the admin API, NOT from the presence payload, so they cannot be
              spoofed by a forged presence entry. Absent means the member has not
              run a client that reports it, NOT a confirmed desktop. */}
          {known?.clients?.includes('android') && (
            <SiAndroid className="h-3.5 w-3.5 flex-shrink-0 text-emerald-400" />
          )}
          {known?.app_version && (
            <span className="flex-shrink-0 rounded border border-white/5 bg-white/[0.04] px-2 py-0.5 font-mono text-[10px] text-textMuted">
              {known.app_version}
            </span>
          )}
        </div>
        );
      })}
      {snapshot.anonKeyCount > 0 && (
        <div className="flex items-center gap-3 p-2 text-sm text-textMuted">
          <div className="h-2 w-2 flex-shrink-0 rounded-full bg-white/20" />
          <span>+ {snapshot.anonKeyCount} anonymous session{snapshot.anonKeyCount === 1 ? '' : 's'}</span>
        </div>
      )}
    </div>
  );
};

// ─── Recent signups ──────────────────────────────────────────────────────────

const RecentSignups = ({ users }: { users: AdminUser[] }) => {
  const recent = useMemo(() => {
    const cutoff = Date.now() - 14 * 24 * 3600_000;
    return [...users]
      .filter(u => new Date(u.created_at).getTime() >= cutoff)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 6);
  }, [users]);

  if (recent.length === 0) return <div className="text-sm text-textMuted">No new users in the last 14 days.</div>;
  return (
    <div className="space-y-2">
      {recent.map(u => (
        <div key={u.id} className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-white/[0.02]">
          <Avatar user={u} sizeClass="w-7 h-7" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm text-textPrimary" title={u.display_name}>{u.display_name}</div>
            <div className="text-xs text-textMuted">joined {formatRelativeTime(u.created_at)}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// `sizeClass` must be a literal Tailwind class pair (e.g. "w-8 h-8") so the
// scanner emits it — never interpolate the size into the class name.
const Avatar = ({ user, sizeClass }: { user: AdminUser; sizeClass: string }) => {
  const base = `${sizeClass} rounded-full ring-1 ring-white/10 flex-shrink-0`;
  return user.avatar_url ? (
    <img src={user.avatar_url} alt="" className={base} />
  ) : (
    <div className={`${base} flex items-center justify-center bg-white/[0.06] text-xs font-bold text-textMuted`}>
      {user.display_name.charAt(0).toUpperCase()}
    </div>
  );
};

// ─── Trend charts (dependency-free inline SVG) ───────────────────────────────

type TrendKind = 'area' | 'line' | 'bars';

const TrendSparkline = ({ points, kind }: { points: number[]; kind: TrendKind }) => {
  const W = 320, H = 72, PAD = 6;
  const max = Math.max(...points, 0);
  const min = Math.min(...points, 0);
  const span = (max - min) || 1;
  const n = points.length;
  const x = (i: number) => (n <= 1 ? W / 2 : PAD + (i * (W - PAD * 2)) / (n - 1));
  const y = (v: number) => PAD + (H - PAD * 2) * (1 - (v - min) / span);
  const accent = 'var(--color-accent)';

  if (kind === 'bars') {
    const slot = (W - PAD * 2) / n;
    const bw = Math.max(1, slot * 0.6);
    return (
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H} style={{ display: 'block' }}>
        {points.map((v, i) => {
          const bx = PAD + i * slot + (slot - bw) / 2;
          const by = y(v);
          return <rect key={i} x={bx} y={by} width={bw} height={Math.max(0, H - PAD - by)} rx={1} style={{ fill: accent, fillOpacity: 0.6 }} />;
        })}
      </svg>
    );
  }

  const line = points.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H} style={{ display: 'block' }}>
      {kind === 'area' && (
        <path d={`${line} L ${x(n - 1).toFixed(1)} ${H - PAD} L ${x(0).toFixed(1)} ${H - PAD} Z`} style={{ fill: accent, fillOpacity: 0.12 }} />
      )}
      <path
        d={line}
        fill="none"
        style={{ stroke: accent }}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

const TrendCard = ({ title, points, kind, format }: {
  title: string;
  points: number[];
  kind: TrendKind;
  format?: (n: number) => string;
}) => {
  const fmt = format ?? formatNumber;
  const latest = points[points.length - 1] ?? 0;
  const first = points[0] ?? 0;
  const delta = latest - first;
  return (
    <div className="settings-card p-5">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-textSecondary">{title}</h3>
        {points.length >= 2 && delta !== 0 && (
          <span className={`inline-flex items-center gap-0.5 text-xs ${delta > 0 ? 'text-emerald-400' : 'text-textMuted'}`}>
            {delta > 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
            {fmt(Math.abs(delta))}
          </span>
        )}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-textPrimary">{fmt(latest)}</div>
      <div className="mt-3">
        <TrendSparkline points={points} kind={kind} />
      </div>
    </div>
  );
};

// ─── User table ──────────────────────────────────────────────────────────────

type SortKey = 'last_seen' | 'display_name' | 'messages_sent' | 'hours_watched' | 'channel_points_collected';
type NumericSortKey = Exclude<SortKey, 'display_name'>;
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 25;

const numericSortValue = (u: AdminUser, key: NumericSortKey): number => {
  switch (key) {
    case 'last_seen': return new Date(u.last_seen).getTime();
    case 'messages_sent': return u.stats?.messages_sent || 0;
    case 'hours_watched': return u.stats?.hours_watched || 0;
    case 'channel_points_collected': return u.stats?.channel_points_collected || 0;
  }
};

const UserTable = ({ users, onlineIds, onMembershipChange }: {
  users: AdminUser[];
  // Liveness only. Descriptive fields come from `users`, never from presence.
  onlineIds: Set<string>;
  onMembershipChange: (uid: string, m: Membership) => void;
}) => {
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<'all' | 'online'>('all');
  const [subFilter, setSubFilter] = useState<'all' | 'subs' | 'gifted'>('all');
  const [versionFilter, setVersionFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('last_seen');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    const v = versionFilter.toLowerCase().trim();
    return users.filter(u => {
      if (q && !u.display_name.toLowerCase().includes(q) && !u.username.toLowerCase().includes(q)) return false;
      if (tab === 'online' && !onlineIds.has(u.id)) return false;
      if (subFilter === 'subs' && !u.membership.is_active) return false;
      if (subFilter === 'gifted' && u.membership.source !== 'gift') return false;
      if (v) {
        // Server-recorded version only. The live presence value was forgeable.
        const ver = (u.app_version || '').toLowerCase();
        if (!ver.includes(v)) return false;
      }
      return true;
    });
  }, [users, query, tab, subFilter, versionFilter, onlineIds]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      if (sortKey === 'display_name') {
        return a.display_name.localeCompare(b.display_name) * dir;
      }
      return (numericSortValue(a, sortKey) - numericSortValue(b, sortKey)) * dir;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageRows = sorted.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  // Look up the selected user from the full list (not just the current page) so
  // the detail panel survives paging/filtering while one is open.
  const selectedUser = expandedId ? users.find(u => u.id === expandedId) ?? null : null;

  const onSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'display_name' ? 'asc' : 'desc'); }
    setPage(0);
  };

  const SortHeader = ({ label, k, right, hide }: { label: string; k: SortKey; right?: boolean; hide?: boolean }) => (
    <th
      onClick={() => onSort(k)}
      className={`cursor-pointer select-none px-5 py-3 text-xs font-semibold uppercase tracking-wider text-textMuted transition-colors hover:text-textSecondary ${right ? 'text-right' : ''} ${hide ? 'hidden lg:table-cell' : ''}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k && (sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
      </span>
    </th>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-textPrimary">
          <Users size={20} className="text-accent" />
          User directory
          <span className="text-sm font-normal text-textMuted">({sorted.length})</span>
        </h2>

        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textMuted" />
            <input
              type="text"
              placeholder="Search name…"
              value={query}
              onChange={e => { setQuery(e.target.value); setPage(0); }}
              className="w-48 rounded-lg border border-white/10 bg-white/[0.03] py-2 pl-9 pr-3 text-sm text-textPrimary outline-none focus:border-accent/50"
            />
          </div>
          <input
            type="text"
            placeholder="Version…"
            value={versionFilter}
            onChange={e => { setVersionFilter(e.target.value); setPage(0); }}
            className="w-28 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-sm text-textPrimary outline-none focus:border-accent/50"
          />
          <div className="flex rounded-lg border border-white/10 bg-white/[0.03] p-1">
            <button
              onClick={() => { setTab('all'); setPage(0); }}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${tab === 'all' ? 'bg-white/[0.08] text-textPrimary' : 'text-textMuted hover:text-textSecondary'}`}
            >All</button>
            <button
              onClick={() => { setTab('online'); setPage(0); }}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${tab === 'online' ? 'bg-white/[0.08] text-emerald-400' : 'text-textMuted hover:text-textSecondary'}`}
            >Online</button>
          </div>
          <div className="flex rounded-lg border border-white/10 bg-white/[0.03] p-1">
            <button
              onClick={() => { setSubFilter('all'); setPage(0); }}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${subFilter === 'all' ? 'bg-white/[0.08] text-textPrimary' : 'text-textMuted hover:text-textSecondary'}`}
            >Any</button>
            <button
              onClick={() => { setSubFilter('subs'); setPage(0); }}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${subFilter === 'subs' ? 'bg-white/[0.08] text-emerald-400' : 'text-textMuted hover:text-textSecondary'}`}
            >Subs</button>
            <button
              onClick={() => { setSubFilter('gifted'); setPage(0); }}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${subFilter === 'gifted' ? 'bg-white/[0.08] text-accent' : 'text-textMuted hover:text-textSecondary'}`}
            >Gifted</button>
          </div>
        </div>
      </div>

      {/* `sn-table-scroll` bounds the height so the horizontal scrollbar is always
          on screen and the header stays put. Without it the scrollbar sat at the
          bottom of a ~1400px-tall container (25 rows), i.e. far below the fold,
          so the columns on the right were simply unreachable. See globals.css. */}
      <div className="glass-panel overflow-hidden rounded-2xl">
        <div className="sn-table-scroll">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-white/[0.02]">
                <th className="w-12 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-textMuted">Live</th>
                <SortHeader label="User" k="display_name" />
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-textMuted">Subscription</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-textMuted">Version</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-textMuted">Client</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-textMuted">Accounts</th>
                <SortHeader label="Messages" k="messages_sent" right hide />
                <SortHeader label="Hours" k="hours_watched" right hide />
                <SortHeader label="Points" k="channel_points_collected" right hide />
                <SortHeader label="Last seen" k="last_seen" />
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  {/* 11 columns: Live, User, Subscription, Version, Client,
                      Accounts, Messages, Hours, Points, Last seen, chevron. */}
                  <td colSpan={11} className="px-5 py-12 text-center text-textMuted">No users match these filters.</td>
                </tr>
              ) : pageRows.map(user => {
                const isOnline = onlineIds.has(user.id);
                // Version and platform come from the server-recorded row, never
                // from the live presence payload, which is forgeable (H14).
                return (
                  <UserRow
                    key={user.id}
                    user={user}
                    isOnline={isOnline}
                    isSelected={expandedId === user.id}
                    displayVersion={user.app_version}
                    onToggle={() => setExpandedId(expandedId === user.id ? null : user.id)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-white/5 px-5 py-3 text-sm">
            <div className="text-textMuted">Page {currentPage + 1} of {totalPages} ({sorted.length} users)</div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="rounded-md p-1.5 transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronLeft size={16} className="text-textSecondary" />
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
                className="rounded-md p-1.5 transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronRight size={16} className="text-textSecondary" />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedUser && (
        <UserDetailPanel
          user={selectedUser}
          onMembershipChange={onMembershipChange}
          onClose={() => setExpandedId(null)}
        />
      )}
    </div>
  );
};

// Summary row only. Clicking it selects the user; the detail + gift controls
// render in UserDetailPanel below the table (outside the horizontal scroll, so
// the membership block can never be cut off on a narrow viewport).
const UserRow = ({ user, isOnline, isSelected, displayVersion, onToggle }: {
  user: AdminUser;
  isOnline: boolean;
  isSelected: boolean;
  displayVersion?: string | null;
  /** Only known while the user is ONLINE. Platform rides the ephemeral presence
   *  payload and the users table has no column for it, so an offline row shows
   *  no mark rather than a stale or guessed one. */
  platform?: string;
  onToggle: () => void;
}) => {
  const pill = membershipPill(user.membership);
  return (
    <tr
      onClick={onToggle}
      className={`group cursor-pointer border-t border-white/5 transition-colors ${isSelected ? 'bg-white/[0.04]' : 'hover:bg-white/[0.02]'}`}
    >
      <td className="px-5 py-3">
        <div className="flex justify-center">
          <div className={`h-2 w-2 rounded-full ${isOnline ? 'bg-emerald-400' : 'bg-white/15'}`} />
        </div>
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <Avatar user={user} sizeClass="w-8 h-8" />
          {/* `title` on both lines: these truncate, and a truncated name with no
              tooltip is data the page is holding and refusing to show. The full
              value is also in the profile panel below. */}
          <div className="min-w-0">
            <div className="truncate font-medium text-textPrimary" title={user.display_name}>
              {user.display_name}
            </div>
            <div className="truncate text-xs text-textMuted" title={`@${user.username}`}>
              @{user.username}
            </div>
          </div>
        </div>
      </td>
      <td className="px-5 py-3">
        {pill.tone === 'none' ? (
          <span className="text-xs text-textMuted">None</span>
        ) : (
          <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${PILL_CLASS[pill.tone]}`}>
            {pill.label}
          </span>
        )}
      </td>
      <td className="px-5 py-3">
        <span className={`inline-flex items-center rounded border border-white/5 px-2 py-0.5 font-mono text-xs font-medium ${isOnline && displayVersion ? 'bg-emerald-400/10 text-emerald-400' : 'bg-white/[0.04] text-textMuted'}`}>
          {displayVersion || 'unknown'}
        </span>
      </td>
      {/* Which client(s) this member runs, and each client's own version.
          Separate from Accounts on purpose: this one answers "is macOS stuck",
          and it can only do that if it is not sharing an icon row with "has a
          Kick account". Words, not marks: there is no recognisable logo for
          "macos-aarch64", and the per-client version is the point. */}
      <td className="px-5 py-3">
        {clientsFor(user).length === 0 ? (
          <span className="text-xs text-textMuted">&mdash;</span>
        ) : (
          <div className="space-y-0.5">
            {clientsFor(user).map(p => {
              const detail = (user.client_details ?? []).find(c => c.platform === p);
              return (
                <div
                  key={p}
                  title={[clientPlatformTitle(p), detail?.arch, detail?.version]
                    .filter(Boolean)
                    .join(' · ')}
                  className="flex items-baseline gap-1.5 whitespace-nowrap text-xs"
                >
                  <span className="text-textSecondary">{clientPlatformLabel(p)}</span>
                  {/* arch only when it is NOT the ordinary one for that platform:
                      every Windows box is x86_64 and printing it on every row is
                      width spent on no information. aarch64 on macOS is the case
                      that actually matters, because it selects a different update
                      artifact. */}
                  {detail?.arch && detail.arch !== 'x86_64' && (
                    <span className="text-textMuted opacity-60">{detail.arch}</span>
                  )}
                  {detail?.version && (
                    <span className="font-mono text-textMuted">{detail.version}</span>
                  )}
                  {detail?.channel === 'debug' && (
                    <span className="rounded bg-amber-400/10 px-1 text-[10px] uppercase text-amber-400">
                      debug
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </td>
      {/* The non-Twitch ACCOUNTS this member has connected. Marks only: the
          column is scanned down rather than read across, and the brand logos are
          recognisable at a glance where the words would be noise. Order is fixed
          by platformsFor so marks line up between rows. */}
      <td className="px-5 py-3">
        <div className="flex items-center gap-1.5" title={platformsFor(user).map(pid => PLATFORM_META[pid]?.label ?? pid).join(', ')}>
          {platformsFor(user).map(pid => {
            const meta = PLATFORM_META[pid];
            const Icon = meta?.icon;
            return Icon ? (
              <Icon key={pid} className="h-3.5 w-3.5 flex-shrink-0" style={{ color: meta.color }} />
            ) : (
              <span key={pid} className="text-xs text-textMuted">{pid}</span>
            );
          })}
        </div>
      </td>
      <td className="hidden px-5 py-3 text-right font-mono text-sm tabular-nums text-textSecondary lg:table-cell">{user.stats ? formatNumber(user.stats.messages_sent) : '0'}</td>
      <td className="hidden px-5 py-3 text-right font-mono text-sm tabular-nums text-textSecondary lg:table-cell">{user.stats ? formatHours(user.stats.hours_watched) : '0'}</td>
      <td className="hidden px-5 py-3 text-right font-mono text-sm tabular-nums text-textMuted lg:table-cell">{user.stats ? formatNumber(user.stats.channel_points_collected) : '0'}</td>
      <td className="px-5 py-3 text-sm text-textMuted">{formatRelativeTime(user.last_seen)}</td>
      <td className="px-5 py-3 text-right">
        {isSelected ? <ChevronUp size={16} className="text-textMuted" /> : <ChevronDown size={16} className="text-textMuted opacity-60 group-hover:opacity-100" />}
      </td>
    </tr>
  );
};

// Full-width detail + gift/revoke controls for the selected user. Rendered below
// the table (not inside its overflow-x-auto), so its 3-column grid never gets
// dragged off-screen by the table's horizontal scroll.
const UserDetailPanel = ({ user, onMembershipChange, onClose }: {
  user: AdminUser;
  onMembershipChange: (uid: string, m: Membership) => void;
  onClose: () => void;
}) => {
  const m = user.membership;
  const pill = membershipPill(m);
  const [term, setTerm] = useState<GiftTerm>(1);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  // Bring the panel into view when a row is selected (it can be far below a row
  // on a later page).
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [user.id]);

  const runAction = async (fn: () => Promise<{ membership: Membership }>) => {
    setBusy(true);
    setActionError(null);
    try {
      const res = await fn();
      onMembershipChange(user.id, res.membership);
    } catch (e) {
      setActionError(e instanceof Error ? e.message.replace(/^membership_failed:/, '') : String(e));
    } finally {
      setBusy(false);
    }
  };

  const doGift = () => runAction(() => giftMembership(user.id, term));
  const doRevoke = () => {
    if (!window.confirm('Revoke this gifted membership? Active status drops immediately; earned badges stay.')) return;
    void runAction(() => revokeMembership(user.id));
  };

  const periodLabel =
    m.source === 'gift'
      ? (m.gift_months_remaining === null ? 'Renews' : 'Active through')
      : (m.cancel_at_period_end ? 'Ends' : 'Renews');
  const badgeList = [
    m.owns_subscriber_badge ? 'Subscriber' : null,
    m.owns_supporter_badge ? 'Supporter' : null,
  ].filter(Boolean).join(', ') || 'None';

  return (
    <div ref={ref} className="settings-card space-y-5 p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar user={user} sizeClass="w-9 h-9" />
          <div className="min-w-0">
            <div className="truncate font-medium text-textPrimary">{user.display_name}</div>
            <div className="truncate text-xs text-textMuted">@{user.username}</div>
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          className="rounded-md p-1.5 text-textMuted transition-colors hover:bg-white/5 hover:text-textSecondary"
        >
          <X size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-widest text-textMuted">Identity</h4>
          <div className="space-y-1.5 text-sm">
            <Field label="ID" value={<span className="font-mono text-textMuted">{user.id}</span>} />
            <Field label="Joined" value={new Date(user.created_at).toLocaleDateString()} />
            <Field label="Last seen" value={new Date(user.last_seen).toLocaleString()} />
            {/* Spelled out here, unlike the table column: the row is scanned so
                it gets marks, the panel is read so it gets words. */}
            <Field
              label="Platforms"
              value={platformsFor(user)
                .map(pid => PLATFORM_META[pid]?.label ?? pid)
                .join(', ')}
            />
          </div>
        </div>
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-widest text-textMuted">Engagement</h4>
          <div className="space-y-1.5 text-sm">
            <Field label="Streams watched" value={user.stats?.streams_watched ?? 0} />
            <Field label="Messages sent" value={user.stats?.messages_sent ?? 0} />
            <Field label="Hours watched" value={formatHours(user.stats?.hours_watched || 0)} />
            <Field label="Points collected" value={formatNumber(user.stats?.channel_points_collected || 0)} />
          </div>
        </div>
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-widest text-textMuted">Membership</h4>
          <div className="space-y-1.5 text-sm">
            <Field
              label="Status"
              value={
                pill.tone === 'none'
                  ? <span className="text-textMuted">None</span>
                  : <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${PILL_CLASS[pill.tone]}`}>{pill.label}</span>
              }
            />
            <Field label="Source" value={m.source === 'stripe' ? 'Paid (Stripe)' : m.source === 'gift' ? 'Gifted' : 'None'} />
            <Field label="Lifetime months" value={m.total_months} />
            {m.first_subscribed_at && (
              <Field label="Member since" value={dateWithDaysAgo(m.first_subscribed_at)} />
            )}
            {m.source === 'stripe' && m.started_at && (
              <Field label="Current sub since" value={dateWithDaysAgo(m.started_at)} />
            )}
            {m.current_period_end && (
              <Field label={periodLabel} value={new Date(m.current_period_end).toLocaleDateString()} />
            )}
            {m.canceled_at && (
              <Field label="Canceled on" value={new Date(m.canceled_at).toLocaleDateString()} />
            )}
            {m.source === 'gift' && <Field label="Gift" value={giftSummary(m)} />}
            {/* The PAID badges only, because they are what gates membership status
                and what a gift does or does not take away. Everything they own is
                in Earned below, with the actual art. */}
            <Field label="Paid badges" value={badgeList} />
          </div>
        </div>
      </div>

      <EarnedAssets user={user} />

      {/* Commands */}
      <div className="flex flex-wrap items-end gap-3 border-t border-white/5 pt-4">
        <label className="flex flex-col gap-1 text-xs text-textMuted">
          Gift term
          <select
            value={term === null ? 'perm' : String(term)}
            onChange={e => setTerm(e.target.value === 'perm' ? null : (Number(e.target.value) as GiftTerm))}
            disabled={busy}
            className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm text-textPrimary outline-none focus:border-accent/50 disabled:opacity-50"
          >
            {GIFT_TERMS.map(t => (
              <option key={t.value === null ? 'perm' : String(t.value)} value={t.value === null ? 'perm' : String(t.value)}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={doGift}
          disabled={busy}
          className="glass-button inline-flex items-center gap-2 px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? <RefreshCw size={14} className="animate-spin" /> : <Gift size={14} />}
          {m.source === 'gift' ? 'Re-gift' : 'Gift membership'}
        </button>
        {m.source === 'gift' && (
          <button
            onClick={doRevoke}
            disabled={busy}
            className="glass-button inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-error disabled:cursor-not-allowed disabled:opacity-50"
          >
            Revoke gift
          </button>
        )}
        <a
          href={`https://twitch.tv/${user.username}`}
          target="_blank"
          rel="noreferrer"
          className="glass-button ml-auto inline-flex items-center gap-2 px-4 py-2 text-sm font-medium"
        >
          <ExternalLink size={14} />
          Open on Twitch
        </a>
        {actionError && <span className="w-full text-xs text-error">Could not update membership: {actionError}</span>}
      </div>
    </div>
  );
};

/** A label/value row in the profile panel.
 *
 *  `min-w-0` + `break-words` on the value, `shrink-0` on the label: this is a
 *  `justify-between` flex row inside a 3-column grid, so without them a long
 *  value (a spelled-out platform list, a cosmetic name, a raw id) pushes the row
 *  wider than its grid cell and gets clipped with nothing to scroll. Wrapping is
 *  the right answer here because the panel is read, not scanned. */
const Field = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-baseline justify-between gap-3 text-textSecondary">
    <span className="shrink-0 text-textMuted">{label}</span>
    <span className="min-w-0 break-words text-right text-textPrimary">{value}</span>
  </div>
);

// ─── Page ────────────────────────────────────────────────────────────────────

export default function Admin() {
  const { me, loading: authLoading, login } = useAuth();

  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [presence, setPresence] = useState<OnlinePresenceSnapshot>(emptyPresenceSnapshot());
  const [windowDays, setWindowDays] = useState<number>(30);
  // Local patches applied after a gift/revoke so the affected row updates without
  // a full reload. Cleared on every full fetch (the server is then authoritative).
  const [membershipOverrides, setMembershipOverrides] = useState<Map<string, Membership>>(new Map());

  const isAdmin = Boolean(me?.is_admin);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetchAdminAnalytics();
      setAnalytics(data);
      setMembershipOverrides(new Map());
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const applyMembership = useCallback((uid: string, m: Membership) => {
    setMembershipOverrides(prev => {
      const next = new Map(prev);
      next.set(uid, m);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    void load();
    const unsub = subscribeToOnlinePresence(setPresence);
    return () => unsub();
  }, [isAdmin, load]);

  const baseUsers = analytics?.users ?? [];
  const users = useMemo(
    () =>
      membershipOverrides.size === 0
        ? baseUsers
        : baseUsers.map(u =>
            membershipOverrides.has(u.id) ? { ...u, membership: membershipOverrides.get(u.id)! } : u,
          ),
    [baseUsers, membershipOverrides],
  );
  const subscriberCount = useMemo(() => users.filter(u => u.membership.is_active).length, [users]);
  const giftedCount = useMemo(() => users.filter(u => u.membership.source === 'gift').length, [users]);
  const series = analytics?.series ?? [];
  const summary = analytics?.membership_summary ?? null;
  const linkedAccounts = analytics?.linked_accounts ?? [];
  const windowed = useMemo(() => series.slice(-windowDays), [series, windowDays]);
  const onlineIds = useMemo(() => new Set(presence.authedUsers.map(u => u.user_id)), [presence]);
  // Server-side directory, keyed by id. Replaces the old `liveById` map of
  // presence payloads: presence now carries liveness only, so anything shown
  // about a member is resolved from here, which the admin API produced under
  // the service role and a client cannot influence.
  const usersById = useMemo(() => {
    const m = new Map<string, AdminUser>();
    for (const u of users) m.set(u.id, u);
    return m;
  }, [users]);
  const activity = useMemo(() => computeActivitySummary(users), [users]);
  // Members who have signed in on streamnook.app, and how many of them have
  // never signed in from an app. From the persisted `web` client row.
  const websiteUsers = useMemo(() => {
    let all = 0;
    let only = 0;
    for (const u of users) {
      const clients = clientsFor(u);
      if (!clients.includes('web')) continue;
      all++;
      if (clients.length === 1) only++;
    }
    return { all, only };
  }, [users]);
  const versions = useMemo(() => computeVersionDistribution(users), [users]);
  // Per-platform, and windowed to the last 30 days. Unwindowed, a client's
  // version is never cleared, so accounts that churned long ago sit in the old
  // buckets forever and the histogram describes "every account that ever existed"
  // rather than the installed base.
  const platformVersions = useMemo(() => computePlatformVersions(users, 30), [users]);
  const updateHealth = useMemo(() => computeUpdateHealth(users, 7), [users]);
  // Windowed like the other trend cards so the toggle moves all four together.
  const adoption = useMemo(() => computeAdoptionCurve(windowed), [windowed]);

  if (authLoading) {
    return <p className="py-16 text-center text-textSecondary">Loading…</p>;
  }

  if (!me) {
    return (
      <div className="space-y-4 py-16 text-center">
        <h1 className="text-2xl font-semibold">Sign in required</h1>
        <button onClick={login} className="glass-button px-5 py-2.5 text-sm font-medium"><TwitchSignInLabel /></button>
      </div>
    );
  }

  // Self-gate. The server is the real guard; this just avoids firing the request
  // and avoids hinting that an admin surface exists.
  if (!isAdmin) {
    return (
      <div className="space-y-3 py-24 text-center">
        <h1 className="text-3xl font-semibold text-textPrimary">Page not found</h1>
        <p className="text-textSecondary">This page does not exist.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg bg-white/[0.04] p-2.5 text-accent">
            <Activity size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-textPrimary">Analytics</h1>
            <p className="text-sm text-textMuted">
              {analytics ? `Snapshot ${formatRelativeTime(analytics.generated_at)}` : 'Loading…'}
              {analytics?.truncated && ' · list capped'}
            </p>
          </div>
        </div>
        <button
          onClick={() => void load()}
          className="glass-button flex items-center gap-2 px-3 py-2 text-sm"
          disabled={isLoading}
        >
          <RefreshCw size={15} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {loadError && (
        <div className="settings-card border border-error/30 p-4 text-sm text-error">
          Could not load analytics: {loadError}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
        <StatCard title="Online now" value={presence.ready ? presence.totalUnique : '…'}
          subtitle={presence.ready ? `${presence.authedUsers.length} signed in${presence.anonKeyCount > 0 ? `, ${presence.anonKeyCount} anon` : ''}` : 'connecting'}
          icon={Radio} />
        <StatCard title="Active (1h)" value={activity.activeLastHour} icon={Clock} />
        <StatCard title="DAU" value={activity.dau} subtitle="last 24h" icon={TrendingUp} />
        <StatCard title="WAU" value={activity.wau} subtitle="last 7 days" icon={TrendingUp} />
        <StatCard title="Total users" value={formatNumber(users.length)} subtitle={`+${activity.newThisWeek} this week`} icon={Users} />
        <StatCard title="Website" value={formatNumber(websiteUsers.all)}
          subtitle={`${formatNumber(websiteUsers.only)} website only`} icon={Globe} />
        <StatCard title="Members" value={summary?.members.total ?? subscriberCount}
          subtitle={summary
            ? `${summary.members.paid} paying · ${summary.members.boost_only + summary.members.gift_only} boost or gift`
            : giftedCount > 0 ? `${giftedCount} gifted` : 'paid + gifted'}
          icon={Crown} />
        <StatCard title="Messages" value={formatNumber(analytics?.global_stats.total_messages_sent ?? 0)}
          subtitle={`${formatHours(analytics?.global_stats.total_hours_watched ?? 0)} hours`} icon={MessageSquare} />
      </div>

      {summary && <AdminMembership summary={summary} usersById={usersById} />}

      {/* Platform accounts. Rendered only when there is something to report:
          before the desktop release that records these, and before the
          user_linked_accounts migration has run, an empty card would read as
          "nobody uses this" rather than as "nothing has been measured yet". */}
      {linkedAccounts.length > 0 && (
        <section className="space-y-3">
          <h2 className="flex items-center gap-2 text-xl font-semibold text-textPrimary">
            <Users size={20} className="text-accent" /> Platform accounts
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {linkedAccounts.map(row => {
              const meta = PLATFORM_META[row.provider];
              const Icon = meta?.icon;
              const dropped = row.ever_connected - row.connected_now;
              return (
                <div key={row.provider} className="settings-card flex items-center gap-3 p-4">
                  {Icon && <Icon className="h-5 w-5 flex-shrink-0" style={{ color: meta.color }} />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-textPrimary">
                      {meta?.label ?? row.provider}
                    </p>
                    {/* Connected now is the headline; the gap between that and
                        ever-connected is the churn, which is the number worth
                        knowing about a brand new feature. */}
                    <p className="text-xs text-textMuted">
                      {formatNumber(row.connected_now)} connected
                      {dropped > 0 && ` · ${formatNumber(dropped)} disconnected`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-xl font-semibold text-textPrimary">
            <TrendingUp size={20} className="text-accent" /> Trends
          </h2>
          <div className="flex rounded-lg border border-white/10 bg-white/[0.03] p-1">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setWindowDays(d)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${windowDays === d ? 'bg-white/[0.08] text-textPrimary' : 'text-textMuted hover:text-textSecondary'}`}
              >{d}d</button>
            ))}
          </div>
        </div>
        {series.length < 2 ? (
          <div className="settings-card p-6 text-sm text-textMuted">
            Not enough history yet. The first daily snapshot was just recorded — trends appear once there are at least two days of data.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <TrendCard title="Active users (DAU)" points={windowed.map(d => d.dau)} kind="area" />
            <TrendCard title="Total users" points={windowed.map(d => d.total_users)} kind="line" />
            <TrendCard title="New signups / day" points={windowed.map(d => d.new_users_24h)} kind="bars" />
            {/* The rollup has stored a version histogram every day since the
                daily_metrics migration and nothing ever selected it, so this curve
                already has months of history behind it the first time it renders. */}
            {adoption.points.length >= 2 && (
              <TrendCard
                title={`On ${adoption.version}`}
                points={adoption.points}
                kind="area"
                format={n => `${Math.round(n)}%`}
              />
            )}
          </div>
        )}
      </section>

      <div className="space-y-6">
        {isLoading && !analytics ? (
          <div className="settings-card flex items-center gap-3 p-8 text-textMuted">
            <RefreshCw size={16} className="animate-spin" /> Loading user directory…
          </div>
        ) : (
          <UserTable users={users} onlineIds={onlineIds} onMembershipChange={applyMembership} />
        )}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <section className="settings-card p-5">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-textSecondary">
              <Radio size={16} className="text-emerald-400" /> Live right now
            </h3>
            <LiveUsersPanel snapshot={presence} usersById={usersById} />
          </section>

          <section className="settings-card p-5">
            <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-textSecondary">
              <Package size={16} className="text-accent" /> Version distribution
            </h3>
            <p className="mb-4 text-xs text-textMuted">
              Every account, from the version recorded at their last sign-in. Desktop and
              Android share this column, so read the per-platform panel below for adoption.
            </p>
            <VersionDistribution rows={versions} total={users.length} />
          </section>

          <section className="settings-card p-5">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-textSecondary">
              <UserPlus size={16} className="text-accent" /> Recent signups
            </h3>
            <RecentSignups users={users} />
          </section>
        </div>

        {/* Adoption and delivery. Two halves of one question: who is on what, and
            for anyone who is behind, why. */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <section className="settings-card p-5 lg:col-span-2">
            <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-textSecondary">
              <Monitor size={16} className="text-accent" /> Adoption by platform
            </h3>
            <p className="mb-4 text-xs text-textMuted">
              One row per client, so a member who uses both a desktop and a phone counts on
              each. Clients active in the last 30 days.
            </p>
            <PlatformVersionPanel platforms={platformVersions} />
          </section>

          <section className="settings-card p-5">
            <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-textSecondary">
              <Download size={16} className="text-accent" /> Update health
            </h3>
            <p className="mb-4 text-xs text-textMuted">
              Why anyone on an old build is on an old build. Hover a row for what it means.
            </p>
            <UpdateHealthPanel health={updateHealth} />
          </section>
        </div>

        <ProfileModeration />
      </div>
    </div>
  );
}

// ─── Link-in-bio pages: reports and the link blocklist ─────────────────────

const reasonLabel = (id: string) => REPORT_REASONS.find(r => r.id === id)?.label ?? id;

interface BlockedHost {
  host: string;
  reason: string | null;
  added_at: string;
}

async function adminCall(path: string, method: string, body?: unknown): Promise<{ ok: boolean; data: unknown }> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  return { ok: res.ok, data: await res.json().catch(() => null) };
}

/** Open reports against members' pages, the actions on them, and the hosts
 *  no link may point to. */
const ProfileModeration = () => {
  const [reports, setReports] = useState<AdminReport[] | null>(null);
  const [hosts, setHosts] = useState<BlockedHost[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newHost, setNewHost] = useState('');
  const [newReason, setNewReason] = useState('');

  const load = useCallback(async () => {
    const [r, h] = await Promise.all([
      adminCall('/api/admin/profile-reports', 'GET'),
      adminCall('/api/admin/link-blocklist', 'GET'),
    ]);
    setReports(r.ok ? ((r.data as { reports?: AdminReport[] }).reports ?? []) : []);
    setHosts(h.ok ? ((h.data as { hosts?: BlockedHost[] }).hosts ?? []) : []);
    if (!r.ok || !h.ok) setError("Couldn't load moderation data.");
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (key: string, path: string, method: string, body: unknown) => {
    setBusy(key);
    setError(null);
    const res = await adminCall(path, method, body);
    if (!res.ok) setError(`That didn't work (${(res.data as { error?: string } | null)?.error ?? 'error'}).`);
    await load();
    setBusy(null);
  };

  // One card per member, newest report first.
  const byMember = useMemo(() => {
    const m = new Map<string, AdminReport[]>();
    for (const r of reports ?? []) m.set(r.userId, [...(m.get(r.userId) ?? []), r]);
    return [...m.values()];
  }, [reports]);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <section className="settings-card p-5 lg:col-span-2">
        <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-textSecondary">
          <Flag size={16} className="text-accent" /> Page reports
          {reports && reports.length > 0 && <span className="text-xs font-normal text-textMuted">{reports.length} open</span>}
        </h3>
        <p className="mb-4 text-xs text-textMuted">
          Reports from visitors of members' pages. Suspend shows only the member's name and picture; it never deletes anything.
        </p>
        {error && <p className="mb-3 text-xs text-error">{error}</p>}
        {reports === null ? (
          <p className="text-sm text-textMuted">Loading…</p>
        ) : byMember.length === 0 ? (
          <p className="text-sm text-textMuted">No open reports.</p>
        ) : (
          <div className="space-y-4">
            {byMember.map(group => {
              const first = group[0];
              const login = first.login ?? first.userId;
              return (
                <div key={first.userId} className="rounded-lg border border-white/[0.06] p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      href={first.login ? profileUrl(first.login) : '#'}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-semibold text-textPrimary hover:underline"
                    >
                      {first.displayName ?? login} <ArrowSquareOut size={12} />
                    </a>
                    <span className="text-xs text-textMuted">
                      @{login} · {group.length} open
                    </span>
                    {first.suspended && <span className="text-xs font-semibold text-warning">Suspended</span>}
                    <span className="ml-auto flex gap-2">
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() =>
                          void act(`s-${first.userId}`, '/api/admin/profile-reports', 'POST', {
                            action: first.suspended ? 'unsuspend' : 'suspend',
                            userId: first.userId,
                            reason: first.suspended ? undefined : group.map(r => r.reason).join(', '),
                          })
                        }
                        className="glass-button inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-error disabled:opacity-50"
                      >
                        <Prohibit size={12} /> {first.suspended ? 'Unsuspend' : 'Suspend'}
                      </button>
                    </span>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {group.map(r => (
                      <li key={r.id} className="flex items-start gap-3 text-xs">
                        <span className="min-w-0 flex-1">
                          <span className="font-semibold text-textSecondary">{reasonLabel(r.reason)}</span>
                          <span className="text-textMuted">
                            {' '}
                            · {new Date(r.createdAt).toLocaleString()} · {r.signedIn ? 'signed in' : 'signed out'}
                          </span>
                          {r.detail && <span className="mt-0.5 block whitespace-pre-line text-textSecondary">{r.detail}</span>}
                        </span>
                        <button
                          type="button"
                          disabled={busy !== null}
                          onClick={() =>
                            void act(`d-${r.id}`, '/api/admin/profile-reports', 'POST', {
                              action: 'dismiss',
                              userId: r.userId,
                              reportId: r.id,
                            })
                          }
                          className="text-textMuted hover:text-textPrimary disabled:opacity-50"
                        >
                          Dismiss
                        </button>
                      </li>
                    ))}
                  </ul>
                  {first.links.length > 0 && (
                    <div className="mt-3 border-t border-white/[0.06] pt-3">
                      <p className="mb-1.5 text-[11px] uppercase tracking-wider text-textMuted">Links on the page</p>
                      <ul className="space-y-1">
                        {first.links.map(l => (
                          <li key={l.id} className="flex items-center gap-2 text-xs">
                            <span className="min-w-0 flex-1 truncate text-textSecondary" title={l.url}>
                              {l.label ? `${l.label} · ` : ''}
                              {l.url}
                            </span>
                            <button
                              type="button"
                              disabled={busy !== null}
                              onClick={() =>
                                void act(`l-${l.id}`, '/api/admin/profile-reports', 'POST', {
                                  action: 'remove_link',
                                  userId: first.userId,
                                  linkId: l.id,
                                })
                              }
                              className="inline-flex items-center gap-1 text-error hover:underline disabled:opacity-50"
                            >
                              <Trash size={12} /> Remove link
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="settings-card p-5">
        <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-textSecondary">
          <Prohibit size={16} className="text-accent" /> Blocked link hosts
        </h3>
        <p className="mb-4 text-xs text-textMuted">No page can save a link to these sites or their subdomains.</p>
        <form
          onSubmit={e => {
            e.preventDefault();
            if (!newHost.trim()) return;
            void act('add-host', '/api/admin/link-blocklist', 'POST', {
              host: newHost,
              reason: newReason || undefined,
            }).then(() => {
              setNewHost('');
              setNewReason('');
            });
          }}
          className="mb-4 space-y-2"
        >
          <input
            value={newHost}
            onChange={e => setNewHost(e.target.value)}
            placeholder="example.com"
            aria-label="Host to block"
            className="glass-input w-full rounded-md px-3 py-1.5 text-sm"
          />
          <input
            value={newReason}
            onChange={e => setNewReason(e.target.value)}
            placeholder="Why (optional)"
            aria-label="Why it is blocked"
            className="glass-input w-full rounded-md px-3 py-1.5 text-sm"
          />
          <button
            type="submit"
            disabled={busy !== null || !newHost.trim()}
            className="glass-button px-3 py-1.5 text-xs disabled:opacity-50"
          >
            Block host
          </button>
        </form>
        {hosts === null ? (
          <p className="text-sm text-textMuted">Loading…</p>
        ) : hosts.length === 0 ? (
          <p className="text-sm text-textMuted">No hosts blocked.</p>
        ) : (
          <ul className="space-y-1.5">
            {hosts.map(h => (
              <li key={h.host} className="flex items-center gap-2 text-xs">
                <span className="min-w-0 flex-1 truncate">
                  <span className="text-textPrimary">{h.host}</span>
                  {h.reason && <span className="text-textMuted"> · {h.reason}</span>}
                </span>
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void act(`rm-${h.host}`, '/api/admin/link-blocklist', 'DELETE', { host: h.host })}
                  className="text-textMuted hover:text-textPrimary disabled:opacity-50"
                >
                  Unblock
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

